/**
 * Deduplicación de peticiones GET idénticas que están en vuelo a la vez.
 *
 * Una carga de listado dispara ~39 llamadas al API pero solo ~25 URLs distintas: el
 * mismo listado de transportistas sale 6 veces, /api/settings 4, el rol del usuario 4.
 * No es un problema de datos viejos —son la misma petición en el mismo instante—, sino
 * de cola: el servidor es de 2 vCPU y cada copia le saca turno a las demás.
 *
 * Esto no cachea nada: la primera sale a la red y las simultáneas esperan su respuesta.
 * En cuanto responde, la siguiente que pidan vuelve a salir. No hay frescura que perder.
 *
 * Por qué una lista explícita y no todos los GET: acá un GET puede tener efectos. Por
 * ejemplo `/api/gotrading/model-definitions/<modelo>?parentID=…` ejecuta el hook
 * `defaultvalues`, que genera números de autoincremento. Compartir una respuesta entre
 * dos de esas podría no ser lo que el llamador espera. Solo entran lecturas puras.
 */

/** Rutas que son lectura pura y se piden repetidas en la misma carga. */
const DEDUPABLE_PATHS = [
  /\/api\/settings$/,
  /\/api\/modules\/active$/,
  /\/api\/migrations\/pending$/,
  /\/api\/roles\/[^/]+$/,
  /\/api\/gotrading\/lists\/type\/[^/]+$/,
  /\/api\/gotrading\/reports$/,
];

// Último recurso, no un tiempo de espera esperable: si una marca quedara suelta, 30 s
// son indistinguibles de un cuelgue para el usuario. Al vencer, las que esperaban salen
// a la red por su cuenta, que es seguro.
const INFLIGHT_TIMEOUT_MS = 10000;

const inflight = new Map();

/**
 * Clave de la petición: URL completa con sus parámetros. Dos llamadas solo se comparten
 * si piden exactamente lo mismo.
 */
export function getDedupeKey(config) {
  if (!config || config.method !== "get" || config.responseType === "blob") {
    return null;
  }

  const url = String(config.url || "");
  if (!url) return null;

  const path = url.split("?")[0];
  if (!DEDUPABLE_PATHS.some((re) => re.test(path))) return null;

  let key = url;
  if (config.params && Object.keys(config.params).length > 0) {
    key = `${url}${url.includes("?") ? "&" : "?"}${new URLSearchParams(
      config.params
    ).toString()}`;
  }
  return key;
}

/** Promesa de la petición ya en vuelo para esta clave, si la hay. */
export function getInflightRequest(key) {
  const pending = key ? inflight.get(key) : null;
  return pending ? pending.promise : null;
}

/** Marca esta petición como la que sale a la red. */
export function markInflightRequest(key) {
  if (!key || inflight.has(key)) return;

  let resolve;
  let reject;
  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });
  // Sin esto, un fallo deja un rechazo sin manejar en consola.
  promise.catch(() => {});

  // Red de seguridad: si la petición nunca se resuelve (pestaña suspendida, request
  // cancelado), las que esperan no pueden quedar colgadas.
  const timer = setTimeout(() => {
    settleInflightRequest(key, null, new Error("request dedupe timeout"));
  }, INFLIGHT_TIMEOUT_MS);

  inflight.set(key, { promise, resolve, reject, timer });
}

export function settleInflightRequest(key, data, error) {
  const pending = key ? inflight.get(key) : null;
  if (!pending) return;

  inflight.delete(key);
  clearTimeout(pending.timer);
  if (error) {
    pending.reject(error);
  } else {
    pending.resolve(data);
  }
}
